import { useState } from 'react';
import type { DirectReportAssessableAttribute, DirectReportAssessmentResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { DirectReportAssessmentForm } from '../components/directReport/DirectReportAssessmentForm';
import { DirectReportWithdrawalForm } from '../components/directReport/DirectReportWithdrawalForm';
import styles from './PeerAssessmentsPage.module.css';

/**
 * Direct-Report Assessment Source — the read and write surface for
 * `GET /direct-report-assessments` and the existing assessment write path
 * (Product Owner decision, 2026-09-19, "Resolve Blocking Product Decisions
 * and Implement the Authorized Batch"). A dedicated nav destination, for
 * the same reason Peer Assessments is one: this data is caller-scoped, not
 * attached to any one employee's profile.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS ALLOWED TO SAY
 *
 * Only who the caller's own manager is (name and id — needed to state
 * whose attributes these are; nothing else about them) and, for each
 * attribute the caller is permitted to assess as a direct report, its
 * scale wording and the caller's own prior rating, if any. No composite,
 * confidence, history, trajectory, Role Fit, other assessor's identity or
 * rating, and no Elevated attribute — none of it exists anywhere upstream
 * of the endpoint.
 *
 * The manager's name is deliberately plain text, never a link to their
 * profile: general profile visibility is not granted by this relationship,
 * and `/people/:employeeId` still enforces the unmodified
 * `authorizeEmployeeAccess` — linking there would send a direct report to
 * a 404 for the one person this page names.
 * ---------------------------------------------------------------------
 */
export function DirectReportAssessmentPage() {
  const state = useApiResource<DirectReportAssessmentResponse>('/direct-report-assessments');
  const [acting, setActing] = useState<{ attributeId: string; mode: 'assess' | 'withdraw' } | null>(null);

  if (state.status === 'loading') return <LoadingState label="Loading your manager assessment…" />;

  if (state.status === 'failed') {
    return <ErrorState title="Could not be loaded" detail={describeError(state.error)} />;
  }

  const { manager } = state.data;

  function finish() {
    setActing(null);
    state.reload();
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Assess My Manager</h1>
      </header>

      {manager === null ? (
        <EmptyState
          title="No manager on record"
          detail="You do not currently have a manager on record, so there is nothing to assess. This follows your current team assignment and changes automatically."
        />
      ) : (
        <>
          <p className={styles.note}>
            Attributes you are permitted to assess for your current manager, named below. Where you have already
            rated an attribute, your own rating is shown alongside it, with the option to correct or withdraw it.
          </p>

          <ul className={styles.list} aria-label="Your manager">
            <li className={styles.group}>
              <span className={styles.person}>{manager.fullName}</span>
              {manager.attributes.length === 0 ? (
                <p className={styles.noAttributes}>No attributes are currently open for you to assess for your manager.</p>
              ) : (
                <ul className={styles.items}>
                  {manager.attributes.map((attribute) => (
                    <AttributeItem
                      key={attribute.id}
                      managerId={manager.id}
                      managerName={manager.fullName}
                      attribute={attribute}
                      acting={acting && acting.attributeId === attribute.id ? acting.mode : null}
                      onAct={(mode) => setActing({ attributeId: attribute.id, mode })}
                      onCancel={() => setActing(null)}
                      onDone={finish}
                    />
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </>
      )}
    </div>
  );
}

function AttributeItem({
  managerId,
  managerName,
  attribute,
  acting,
  onAct,
  onCancel,
  onDone,
}: {
  managerId: string;
  managerName: string;
  attribute: DirectReportAssessableAttribute;
  acting: 'assess' | 'withdraw' | null;
  onAct: (mode: 'assess' | 'withdraw') => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  return (
    <li className={styles.item}>
      <div className={styles.attributeHead}>
        <span className={styles.attribute}>{attribute.name}</span>
        <span className={styles.rating}>
          {attribute.ownRating === null ? (
            <span className={styles.notRated}>You haven&rsquo;t rated this yet</span>
          ) : (
            `Your rating: ${attribute.ownRating}`
          )}
        </span>
      </div>
      <p className={styles.scaleHint}>
        <span>1 — {attribute.lowEndMeaning}</span>
        <span>20 — {attribute.highEndMeaning}</span>
      </p>

      {acting === null ? (
        <span className={styles.actions}>
          <button type="button" className={styles.action} onClick={() => onAct('assess')}>
            {attribute.ownAssessmentId === null ? 'Rate' : 'Correct'}
          </button>
          {attribute.ownAssessmentId !== null ? (
            <button type="button" className={styles.action} onClick={() => onAct('withdraw')}>
              Withdraw
            </button>
          ) : null}
        </span>
      ) : null}

      {acting === 'assess' ? (
        <DirectReportAssessmentForm
          employeeId={managerId}
          managerName={managerName}
          attribute={attribute}
          onRecorded={onDone}
          onClose={onCancel}
        />
      ) : null}

      {acting === 'withdraw' && attribute.ownAssessmentId !== null && attribute.ownRating !== null ? (
        <DirectReportWithdrawalForm
          employeeId={managerId}
          attributeId={attribute.id}
          attributeName={attribute.name}
          assessmentId={attribute.ownAssessmentId}
          currentRating={attribute.ownRating}
          onWithdrawn={onDone}
          onClose={onCancel}
        />
      ) : null}
    </li>
  );
}
